import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Building2, Camera, CheckCircle2, Eye, EyeOff, Lock, Mail, MapPin, Phone, Search, UserRound } from "lucide-react";
import { registerCompany } from "../../redux/userSlice";
import { api } from "../../axios/api";
import { notify } from "../../utils/toastAlert";
import AuthShell from "./AuthShell";

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.user);
  const [form, setForm] = useState({
    empresa_nome: "",
    nif: "",
    telefone: "",
    telefone_alternativo: "",
    endereco: "",
    pais: "Angola",
    provincia: "",
    cidade: "",
    codigo_postal: "",
    website: "",
    regime_tributario: "",
    nome: "",
    email: "",
    senha: "",
  });
  const [nifLookup, setNifLookup] = useState({
    loading: false,
    consultedNif: "",
    message: "",
    error: "",
  });
  const [logotipo, setLogotipo] = useState(null);
  const [foto, setFoto] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    document.body.classList.remove("bg-primary", "d-flex", "justify-content-center", "align-items-center", "min-vh-100", "p-5");
    document.body.classList.add("auth-body");
    return () => document.body.classList.remove("auth-body");
  }, []);

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
    if (event.target.name === "nif") {
      setNifLookup({ loading: false, consultedNif: "", message: "", error: "" });
    }
  };

  const normalizeNifData = (payload) => {
    const data = payload?.contribuinte || payload?.data?.ObterContribuinte?.contribuinte || payload?.data?.contribuinte || payload?.data || payload || {};
    const nome =
      data.nome ||
      data.name ||
      data.designacaoSocial ||
      data.razaoSocial ||
      data.denominacao ||
      "";

    return {
      nome,
      nif: data.numeroNIF || data.numeroDocumento || data.nif || data.taxRegistrationNumber || form.nif,
      pais: data.residenciaFiscal === "AO" ? "Angola" : data.residenciaFiscal || form.pais,
      status: data.estadoContribuinte || data.estado || "",
      regime_tributario: data.regimeIva || data.regime_iva || data.regimeTributario || data.regime_tributario || form.regime_tributario,
    };
  };

  const consultNif = async () => {
    const nif = form.nif.trim();

    if (!nif) {
      setNifLookup({ loading: false, consultedNif: "", message: "", error: "Informe o NIF para consultar." });
      return false;
    }

    setNifLookup({ loading: true, consultedNif: "", message: "", error: "" });

    try {
      const response = await api.get(`/nif/${encodeURIComponent(nif)}`);

      const normalized = normalizeNifData(response.data);
      setForm((current) => ({
        ...current,
        nif: normalized.nif || current.nif,
        empresa_nome: normalized.nome || current.empresa_nome,
        pais: normalized.pais || current.pais,
        regime_tributario: normalized.regime_tributario || current.regime_tributario,
      }));
      setNifLookup({
        loading: false,
        consultedNif: nif,
        message: normalized.nome ? `NIF validado: ${normalized.nome}` : "NIF consultado com sucesso.",
        error: "",
      });
      return normalized;
    } catch (error) {
      setNifLookup({
        loading: false,
        consultedNif: "",
        message: "",
        error: error.message || "Não foi possível consultar o NIF.",
      });
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let companyForm = form;

    if (form.nif && nifLookup.consultedNif !== form.nif.trim()) {
      const nifData = await consultNif();
      if (!nifData) return;
      companyForm = {
        ...form,
        nif: nifData.nif || form.nif,
        empresa_nome: nifData.nome || form.empresa_nome,
        pais: nifData.pais || form.pais,
        regime_tributario: nifData.regime_tributario || form.regime_tributario,
      };
    }

    if (!companyForm.empresa_nome.trim()) {
      setNifLookup({
        loading: false,
        consultedNif: "",
        message: "",
        error: "Não foi possível obter o nome da empresa. Consulte o NIF ou preencha a empresa manualmente.",
      });
      return;
    }

    const data = new FormData();
    const empresa = {
      nome: companyForm.empresa_nome,
      nif: companyForm.nif,
      telefone: companyForm.telefone,
      telefone_alternativo: companyForm.telefone_alternativo,
      email: companyForm.email,
      endereco: companyForm.endereco,
      pais: companyForm.pais,
      provincia: companyForm.provincia,
      cidade: companyForm.cidade,
      codigo_postal: companyForm.codigo_postal,
      website: companyForm.website,
      regime_tributario: companyForm.regime_tributario || "",
    };
    const user = {
      nome: form.nome,
      email: form.email,
      telefone: form.telefone,
      senha: form.senha,
    };

    Object.entries(empresa).forEach(([key, value]) => data.append(`empresa[${key}]`, value));
    Object.entries(user).forEach(([key, value]) => data.append(`user[${key}]`, value));
    if (logotipo) data.append("logotipo", logotipo);
    if (foto) data.append("foto", foto);

    const result = await dispatch(registerCompany(data));

    if (registerCompany.fulfilled.match(result)) {
      await notify("Empresa criada com sucesso.", "success");
      navigate("/dashboard/main", { replace: true });
    } else {
      await notify(result.payload || "Não foi possível criar a empresa.", "error");
    }
  };

  return (
    <AuthShell
      size="md"
      eyebrow="Criar empresa"
      title="Cadastro"
      subtitle="Preencha os dados da empresa e do administrador."
      footer={(
        <p>Já tem conta? <Link to="/login">Entrar</Link></p>
      )}
    >
      <div className="auth-card-header">
        <span>Cadastro</span>
        <h2>Empresa e administrador</h2>
      </div>

      <div className="auth-progress">
        <span><CheckCircle2 size={14} /> Empresa</span>
        <span><CheckCircle2 size={14} /> Fiscal</span>
        <span><CheckCircle2 size={14} /> Admin</span>
      </div>

      <form className="auth-form" onSubmit={handleSubmit}>
        {error && <div className="auth-alert auth-alert-danger">{error}</div>}
        {nifLookup.error && <div className="auth-alert auth-alert-warning">{nifLookup.error}</div>}
        {nifLookup.message && <div className="auth-alert auth-alert-success">{nifLookup.message}</div>}

        <div className="auth-section-title">Dados da empresa</div>
        <div className="auth-grid auth-grid-2">
          <div className="auth-field">
            <label htmlFor="empresa_nome">Empresa</label>
            <div className="auth-input-wrap">
              <Building2 size={18} />
              <input type="text" id="empresa_nome" name="empresa_nome" value={form.empresa_nome} onChange={handleChange} placeholder="Preenchido pela consulta do NIF" />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="nif">NIF</label>
            <div className="auth-input-wrap auth-input-action">
              <input type="text" id="nif" name="nif" value={form.nif} onChange={handleChange} required placeholder="NIF da empresa" />
              <button type="button" onClick={consultNif} disabled={nifLookup.loading}>
                <Search size={16} />
                {nifLookup.loading ? "..." : "Consultar"}
              </button>
            </div>
          </div>
        </div>

        <div className="auth-grid auth-grid-2">
          <div className="auth-field">
            <label htmlFor="regime_tributario">Regime tributário</label>
            <div className="auth-input-wrap">
              <select id="regime_tributario" name="regime_tributario" value={form.regime_tributario} onChange={handleChange}>
                <option value="">Selecionar</option>
                <option value="REGIME GERAL">REGIME GERAL</option>
                <option value="REGIME SIMPLIFICADO">REGIME SIMPLIFICADO</option>
                <option value="REGIME DE EXCLUSÃO">REGIME DE EXCLUSÃO</option>
              </select>
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="website">Website</label>
            <div className="auth-input-wrap">
              <input type="url" id="website" name="website" value={form.website} onChange={handleChange} placeholder="https://empresa.ao" />
            </div>
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="endereco">Endereço</label>
          <div className="auth-input-wrap">
            <MapPin size={18} />
            <input type="text" id="endereco" name="endereco" value={form.endereco} onChange={handleChange} placeholder="Rua, bairro ou referência" />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="logotipo">Logotipo da empresa</label>
          <div className="auth-input-wrap">
            <Camera size={18} />
            <input type="file" id="logotipo" name="logotipo" accept="image/png,image/jpeg,image/webp" onChange={(event) => setLogotipo(event.target.files?.[0] || null)} />
          </div>
        </div>

        <div className="auth-grid auth-grid-3">
          <div className="auth-field">
            <label htmlFor="pais">País</label>
            <div className="auth-input-wrap">
              <input type="text" id="pais" name="pais" value={form.pais} onChange={handleChange} />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="provincia">Província</label>
            <div className="auth-input-wrap">
              <input type="text" id="provincia" name="provincia" value={form.provincia} onChange={handleChange} />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="cidade">Cidade</label>
            <div className="auth-input-wrap">
              <input type="text" id="cidade" name="cidade" value={form.cidade} onChange={handleChange} />
            </div>
          </div>
        </div>

        <div className="auth-section-title">Administrador</div>
        <div className="auth-grid auth-grid-2">
          <div className="auth-field">
            <label htmlFor="nome">Nome</label>
            <div className="auth-input-wrap">
              <UserRound size={18} />
              <input type="text" id="nome" name="nome" value={form.nome} onChange={handleChange} required placeholder="Nome do responsável" />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="email">Email</label>
            <div className="auth-input-wrap">
              <Mail size={18} />
              <input type="email" id="email" name="email" value={form.email} onChange={handleChange} required placeholder="email@empresa.com" />
            </div>
          </div>
        </div>

        <div className="auth-grid auth-grid-3">
          <div className="auth-field">
            <label htmlFor="telefone">Telefone</label>
            <div className="auth-input-wrap">
              <Phone size={18} />
              <input type="text" id="telefone" name="telefone" value={form.telefone} onChange={handleChange} placeholder="Opcional" />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="telefone_alternativo">Telefone alternativo</label>
            <div className="auth-input-wrap">
              <input type="text" id="telefone_alternativo" name="telefone_alternativo" value={form.telefone_alternativo} onChange={handleChange} placeholder="Opcional" />
            </div>
          </div>
          <div className="auth-field">
            <label htmlFor="codigo_postal">Código postal</label>
            <div className="auth-input-wrap">
              <input type="text" id="codigo_postal" name="codigo_postal" value={form.codigo_postal} onChange={handleChange} placeholder="Opcional" />
            </div>
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="senha">Senha</label>
          <div className="auth-input-wrap">
            <Lock size={18} />
            <input
              type={showPassword ? "text" : "password"}
              id="senha"
              name="senha"
              value={form.senha}
              onChange={handleChange}
              required
              placeholder="Crie uma senha"
              autoComplete="new-password"
            />
            <button
              className="auth-icon-button"
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
          <div className="auth-password-hint">
            Use uma senha unica com letras, numeros e pelo menos 8 caracteres.
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="foto">Foto do administrador</label>
          <div className="auth-input-wrap">
            <Camera size={18} />
            <input type="file" id="foto" name="foto" accept="image/png,image/jpeg,image/webp" onChange={(event) => setFoto(event.target.files?.[0] || null)} />
          </div>
        </div>

        <button className="auth-primary-button" type="submit" disabled={loading}>
          <Building2 size={18} />
          {loading ? "A criar..." : "Criar empresa"}
        </button>
      </form>
    </AuthShell>
  );
};

export default Register;
